import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { useState, useRef, useCallback } from 'react';
import Fuse from 'fuse.js';
import PropTypes from '../../../node_modules/prop-types/index.js';
import Errors from '../Errors/Errors.js';
import Label from '../Label/Label.js';
import i18n from '../../lang/i18n/index.js';
import { safeRest, hasErrors, noop } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { INPUT_VARIANT, ADORNMENT_POSITION, TEXT_ALIGN } from './constants.js';
import Datalist from './Datalist.js';
import InputAdornment from './InputAdornment.js';
import ShowPasswordToggle from './ShowPasswordToggle.js';
import { Root, LabelWrapper, LabelTooltipWrapper, TooltipContainer, OptionalText, FlexBox, Input, InputIcon, DisabledInputWrapper, InputWrapper } from './styledComponents.js';
import { FORMAT, buildHandler } from './utils.js';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useLang from '../../hooks/useLang.js';
import useTheme from '../../hooks/useTheme.js';
import useToggle from '../../hooks/useToggle.js';
import useOnClickOutside from '../../hooks/useOnClickOutside.js';
import componentWithName from '../../prop-types/componentWithName.js';

var _excluded = ["id", "label", "value", "variant", "tooltip", "icon", "isIconClickable", "adornment", "adornmentPosition", "forceUpper", "isPassword", "mask", "maskChar", "formatChars", "textAlign", "datalist", "autoComplete", "required", "requiredDecorator", "ariaLabel", "ariaRequired", "ariaDescribedBy", "disabled", "errors", "outline", "showOptionalLabel", "callouts", "onChange", "onBlur", "onClick", "onFocus", "onKeyDown", "customStyle", "searchThreshold"];
var TextInput = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var _newList;
  var controlledId = _ref.id,
    label = _ref.label,
    value = _ref.value,
    variant = _ref.variant,
    tooltip = _ref.tooltip,
    icon = _ref.icon,
    isIconClickable = _ref.isIconClickable,
    adornment = _ref.adornment,
    adornmentPosition = _ref.adornmentPosition,
    forceUpper = _ref.forceUpper,
    isPassword = _ref.isPassword,
    mask = _ref.mask,
    maskChar = _ref.maskChar,
    formatChars = _ref.formatChars,
    textAlign = _ref.textAlign,
    defaultDatalist = _ref.datalist,
    autoComplete = _ref.autoComplete,
    required = _ref.required,
    requiredDecorator = _ref.requiredDecorator,
    ariaLabel = _ref.ariaLabel,
    ariaRequired = _ref.ariaRequired,
    ariaDescribedBy = _ref.ariaDescribedBy,
    disabled = _ref.disabled,
    errors = _ref.errors,
    outline = _ref.outline,
    showOptionalLabel = _ref.showOptionalLabel,
    callouts = _ref.callouts,
    onChange = _ref.onChange,
    _onBlur = _ref.onBlur,
    onClick = _ref.onClick,
    _onFocus = _ref.onFocus,
    onKeyDown = _ref.onKeyDown,
    customStyle = _ref.customStyle,
    searchThreshold = _ref.searchThreshold,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useDerivedState = useDerivedState(useId('input-id-'), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var lang = useLang();
  var theme = useTheme();
  var safeProps = safeRest(rest);
  var _safeCustomStyle = safeCustomStyle(customStyle),
    rootStyle = _safeCustomStyle.rootStyle,
    inputFlexWrapperStyle = _safeCustomStyle.inputFlexWrapperStyle,
    inputWrapperStyle = _safeCustomStyle.inputWrapperStyle,
    inputStyle = _safeCustomStyle.inputStyle;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    focused = _useState2[0],
    setFocused = _useState2[1];
  var _useToggle = useToggle(false),
    _useToggle2 = _slicedToArray(_useToggle, 2),
    pVisible = _useToggle2[0],
    pVisibleToggle = _useToggle2[1];
  var _useState3 = useState(-1),
    _useState4 = _slicedToArray(_useState3, 2),
    selected = _useState4[0],
    setSelected = _useState4[1];
  var _useToggle3 = useToggle(),
    _useToggle4 = _slicedToArray(_useToggle3, 3),
    expanded = _useToggle4[0],
    toggleExpanded = _useToggle4[1],
    setExpanded = _useToggle4[2];
  var _useState5 = useState(defaultDatalist),
    _useState6 = _slicedToArray(_useState5, 2),
    datalist = _useState6[0],
    setDatalist = _useState6[1];
  var datalistRef = useRef(null);
  useOnClickOutside(datalistRef, function () {
    return setExpanded(false);
  });
  var newList = datalist;
  var options = {
    threshold: searchThreshold
  };
  if (datalist && (value === null || value === void 0 ? void 0 : value.length) > 0) {
    var fuse = new Fuse(datalist, options);
    var results = fuse === null || fuse === void 0 ? void 0 : fuse.search(value);
    newList = results.map(function (obj) {
      return obj.item;
    });
  }
  var hasDataList = (datalist === null || datalist === void 0 ? void 0 : datalist.length) > 0;

  // memoize the onChange since we don't need a new copy if mask/props onChange don't change
  var memoizedOnChange = useCallback(function (event) {
    if (!datalist) {
      handle(onChange)(event);
    } else {
      setFocused(false);
      setExpanded(true);
      handle(onChange)(event, datalist.value);
    }
  }, [onChange, datalist, setExpanded]);
  var handleChange = useCallback(function (event, ind) {
    if (newList.length > ind) {
      setSelected(ind);
      handle(onChange)(event, newList[ind].value); // callback with actual value of selected item
    }
  }, [newList, onChange]);
  var selectOption = useCallback(function (event, ind) {
    if (newList.length > ind) {
      handle(onChange)(event, newList[ind]);
      setSelected(-1);
    }
  }, [newList, onChange]);
  var handleItemClick = useCallback(function (event, ind) {
    setExpanded(false);
    selectOption(event, ind);
  }, [setExpanded, selectOption]);
  var handleKeyboardEvent = useCallback(function (e) {
    var key = e.key;
    var handler = buildHandler(e, newList, selected, handleChange, setExpanded, toggleExpanded);
    if (handler[key]) {
      e.preventDefault(); // only prevent default if part of bound keys
      handler[key]();
    }
    if (key === 'Enter') {
      selectOption(e, selected);
    }
  }, [handleChange, newList, selectOption, selected, setExpanded, toggleExpanded]);
  var handleInputClick = useCallback(function () {
    setExpanded(true);
  }, [setExpanded]);
  var Wrapper = disabled ? DisabledInputWrapper : InputWrapper;
  return /*#__PURE__*/React.createElement(Root, {
    rootStyle: rootStyle
  }, label && /*#__PURE__*/React.createElement(LabelWrapper, null, /*#__PURE__*/React.createElement(LabelTooltipWrapper, null, /*#__PURE__*/React.createElement(Label, {
    htmlFor: id,
    customStyle: {
      labelStyle: customStyle.labelStyle
    }
  }, label, "\xA0", requiredDecorator), tooltip && /*#__PURE__*/React.createElement(TooltipContainer, null, tooltip)), !required && showOptionalLabel && /*#__PURE__*/React.createElement(OptionalText, {
    theme: theme
  }, i18n[lang].common.labels.optional)), /*#__PURE__*/React.createElement(FlexBox, {
    inputFlexWrapperStyle: inputFlexWrapperStyle
  }, /*#__PURE__*/React.createElement(Wrapper, {
    focused: focused,
    error: outline || hasErrors(errors),
    inputWrapperStyle: inputWrapperStyle,
    variant: variant,
    theme: theme,
    "data-testid": "input-wrapper"
  }, /*#__PURE__*/React.createElement(Input, _extends({
    id: id,
    "data-testid": "text-input",
    type: isPassword && !pVisible ? 'password' : 'text',
    onBlur: function onBlur(event) {
      setFocused(false);
      handle(_onBlur)(event);
    },
    onChange: memoizedOnChange,
    onFocus: function onFocus(event) {
      setFocused(true);
      handle(_onFocus)(event);
    },
    "aria-label": ariaLabel,
    "aria-required": ariaRequired,
    "aria-describedby": hasErrors(errors) ? "".concat(id, "-errors") : ariaDescribedBy,
    mask: mask,
    maskChar: maskChar,
    value: value,
    forceUpper: forceUpper,
    list: hasDataList ? "".concat(id, "-list") : null,
    autoComplete: isPassword ? autoComplete || 'off' : autoComplete,
    icon: icon,
    isPassword: isPassword,
    textAlign: textAlign,
    inputStyle: inputStyle,
    required: required,
    disabled: disabled,
    adornment: adornment,
    adornmentPosition: adornmentPosition,
    ref: ref,
    formatChars: formatChars,
    theme: theme,
    onKeyDown: hasDataList ? handleKeyboardEvent : onKeyDown,
    onClick: hasDataList ? handleInputClick : onClick,
    "aria-activedescendant": (_newList = newList) !== null && _newList !== void 0 && _newList[selected] ? newList[selected] : ''
  }, safeProps)), adornment && /*#__PURE__*/React.createElement(InputAdornment, {
    variant: variant,
    adornment: adornment,
    hasIcon: icon,
    position: adornmentPosition,
    customStyle: {
      adornmentStyle: customStyle.adornmentStyle
    }
  }), icon && !isPassword && /*#__PURE__*/React.createElement(InputIcon, {
    variant: variant,
    isIconClickable: isIconClickable
  }, icon), isPassword && /*#__PURE__*/React.createElement(ShowPasswordToggle, {
    id: "pwd-toggle-".concat(id),
    show: pVisible,
    toggle: pVisibleToggle,
    theme: theme
  })), hasDataList && expanded && /*#__PURE__*/React.createElement(Datalist, {
    id: id,
    datalist: newList,
    autoComplete: autoComplete,
    value: value,
    selected: selected,
    handleItemClick: handleItemClick,
    handleUpdateDatalist: setDatalist,
    ref: datalistRef
  }), /*#__PURE__*/React.createElement(Errors, {
    id: id,
    callouts: callouts,
    errors: errors
  })));
});
process.env.NODE_ENV !== "production" ? TextInput.propTypes = {
  /** DOM element id */
  id: PropTypes.string,
  /** label component for input */
  label: PropTypes.node,
  /** input's current value (controlled input) */
  value: PropTypes.string,
  /** Which style of input to use */
  variant: PropTypes.oneOf([INPUT_VARIANT.BASIC, INPUT_VARIANT.BASIC_LINE]),
  /** [Tooltip](/docs/tooltip) displayed beside the label */
  tooltip: componentWithName('Tooltip'),
  /** Icon component to use on text input */
  icon: PropTypes.node,
  /** enable click-events for icon by changing 'pointer-events' from 'none' to 'auto' */
  isIconClickable: PropTypes.bool,
  /** adornment character */
  adornment: PropTypes.string,
  /** adornment positioning (left or right) */
  adornmentPosition: PropTypes.oneOf([ADORNMENT_POSITION.LEFT, ADORNMENT_POSITION.RIGHT]),
  /** apply CSS text-transform "uppercase" */
  forceUpper: PropTypes.bool,
  /** is a password field (should show password toggle, set type & autocomplete) */
  isPassword: PropTypes.bool,
  /** input mask (alpha)
   * Accepted formats: https://github.com/sanniassin/react-input-mask#mask
   * (ex. for postal code A1B 2C3, the mask would be "a9a 9a9")
   */
  mask: PropTypes.string,
  /** input mask for unfilled spaces that matches mask, uses a single char such as _  default="" */
  maskChar: PropTypes.string,
  /**
   * Configuration to override default mask format characters
   * Defaults to TextInput.FORMAT
   * More info: https://www.npmjs.com/package/react-input-mask#formatchars--object
   */
  formatChars: PropTypes.shape({
    '[<char>]': PropTypes.string
  }),
  /** Alignment of text in the input */
  textAlign: PropTypes.oneOf([TEXT_ALIGN.LEFT, TEXT_ALIGN.RIGHT, TEXT_ALIGN.CENTER]),
  /** associated datalist items */
  datalist: PropTypes.arrayOf(PropTypes.string),
  /** Fuse.js fuzzy search threshold, used with datalist. 0.0 is a perfect match. 1.0 matches anything */
  searchThreshold: PropTypes.number,
  /** HTML 5 autocomplete attribute */
  autoComplete: PropTypes.string,
  /** HTML 5 required attribute */
  required: PropTypes.bool,
  /** text that is appended to label for elements marked as required */
  requiredDecorator: PropTypes.string,
  /** global attribute aria-label */
  ariaLabel: PropTypes.string,
  /** global attribute aria-required */
  ariaRequired: PropTypes.bool,
  /** DOM element ID for aria-describedBy on input */
  ariaDescribedBy: PropTypes.string,
  /** HTML 5 disabled attribute */
  disabled: PropTypes.bool,
  /** error array */
  errors: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    // error id
    msg: PropTypes.any,
    // localized error text
    isError: PropTypes.bool // error condition
  })),

  /** use error styling without displaying errors; useful if your app needs to handle errors itself */
  outline: PropTypes.bool,
  /** should the 'Optional' label be displayed */
  showOptionalLabel: PropTypes.bool,
  /** sets aria-live to 'polite' and role to 'alert' on error message block  */
  callouts: PropTypes.bool,
  /** HTML 5 autofocus attribute */
  autoFocus: PropTypes.bool,
  /** HTML maxlength attribute, specifies the maximum number of characters that can be entered in a text-type input */
  maxLength: PropTypes.number,
  /** HTML 5 placeholder attribute */
  placeholder: PropTypes.string,
  /** HTML 5 readonly attribute */
  readOnly: PropTypes.bool,
  /** onfocus handler for input */
  onFocus: PropTypes.func,
  /** onblur handler for input */
  onBlur: PropTypes.func,
  /** onchange handler for input */
  onChange: PropTypes.func,
  /** Style overrides */
  customStyle: PropTypes.shape({
    rootStyle: PropTypes.object,
    inputFlexWrapperStyle: PropTypes.object,
    // the wrapper without the label
    inputWrapperStyle: PropTypes.object,
    // the wrapper with the underline
    inputStyle: PropTypes.object,
    // the html input element
    labelStyle: PropTypes.object,
    // label
    adornmentStyle: PropTypes.object
  })
} : void 0;
TextInput.defaultProps = {
  id: undefined,
  label: '',
  value: undefined,
  variant: INPUT_VARIANT.BASIC,
  tooltip: undefined,
  icon: undefined,
  isIconClickable: false,
  adornment: '',
  adornmentPosition: ADORNMENT_POSITION.LEFT,
  forceUpper: false,
  isPassword: false,
  mask: undefined,
  maskChar: '',
  formatChars: FORMAT,
  textAlign: TEXT_ALIGN.LEFT,
  datalist: undefined,
  autoComplete: '',
  searchThreshold: 0.6,
  required: false,
  requiredDecorator: '',
  ariaLabel: '',
  ariaRequired: false,
  ariaDescribedBy: undefined,
  disabled: false,
  errors: [],
  outline: false,
  showOptionalLabel: false,
  callouts: false,
  autoFocus: false,
  maxLength: undefined,
  placeholder: undefined,
  readOnly: false,
  onChange: noop,
  onBlur: noop,
  onFocus: noop,
  customStyle: {
    rootStyle: {},
    inputWrapperStyle: {},
    // the wrapper with the underline
    inputStyle: {},
    // the html input element
    labelStyle: {},
    // label
    adornmentStyle: {}
  }
};
process.env.NODE_ENV !== "production" ? TextInput.displayName = 'TextInput' : void 0;
var TextInput$1 = TextInput;

export { TextInput$1 as default };
